import React, { useState, useEffect } from 'react';
import { track } from './../analytics.js';

// Landing page for the "View Postcard PDF" link in the receipt email.
// Polls /pdf-url every couple seconds until Lob has finished rendering the
// PDF, then redirects to it. Lob's postcard.url is returned synchronously
// from /postcards but the file behind it is rendered asynchronously, so a
// direct link from the receipt email frequently 404s for users who click
// quickly. This page closes that gap with a loader instead of an error.

const POLL_INTERVAL_MS = 2500;
const TIMEOUT_MS = 90 * 1000;

export default function PdfReady({ postcardId }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'timeout' | 'error'
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    track('pdf_redirect_viewed', { postcard_id: postcardId });

    let cancelled = false;
    const startedAt = Date.now();
    let timer = null;

    const poll = () => {
      if (cancelled) return;

      fetch(process.env.REACT_APP_BACKEND_URL + '/pdf-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': process.env.REACT_APP_FRONTEND_ORIGIN,
        },
        body: JSON.stringify({ postcardId }),
        mode: 'cors',
        credentials: 'same-origin',
        cache: 'no-cache',
      })
        .then(res => res.json())
        .then(json => {
          if (cancelled) return;
          if (json.result === 'error') {
            setErrorMsg(json.message || 'Could not look up postcard.');
            setStatus('error');
            return;
          }
          if (json.ready && json.url) {
            track('pdf_redirect_ready', { postcard_id: postcardId });
            window.location.href = json.url;
            return;
          }
          if (Date.now() - startedAt > TIMEOUT_MS) {
            setStatus('timeout');
            return;
          }
          timer = setTimeout(poll, POLL_INTERVAL_MS);
        })
        .catch(() => {
          if (cancelled) return;
          if (Date.now() - startedAt > TIMEOUT_MS) {
            setStatus('timeout');
            return;
          }
          timer = setTimeout(poll, POLL_INTERVAL_MS);
        });
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [postcardId]);

  return (
    <div className="App" style={{ maxWidth: 560, margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
      <style>{`
        @keyframes pdf-spin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .pdf-spinner {
          width: 56px;
          height: 56px;
          border: 4px solid #e5e7eb;
          border-top-color: #0078d4;
          border-radius: 50%;
          margin: 0 auto 24px;
          animation: pdf-spin 1s linear infinite;
        }
      `}</style>

      {status === 'loading' && (
        <>
          <div className="pdf-spinner" aria-hidden="true" />
          <h1 style={{ fontSize: 22, margin: '0 0 12px' }}>Preparing your postcard PDF&hellip;</h1>
          <p style={{ color: '#666', lineHeight: 1.5 }}>
            Your postcard is being rendered. This usually takes a few seconds &mdash; we'll send you over as soon as it's ready.
          </p>
          <p style={{ color: '#999', fontSize: 13, marginTop: 24 }}>
            Reference: <code>{postcardId}</code>
          </p>
        </>
      )}

      {status === 'timeout' && (
        <>
          <h1 style={{ fontSize: 22, margin: '0 0 12px' }}>Still rendering&hellip;</h1>
          <p style={{ color: '#666', lineHeight: 1.5 }}>
            Your postcard PDF is taking longer than usual to render. Please refresh this page in a minute or two.
          </p>
          <p style={{ marginTop: 24 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#0078d4',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 4,
                fontSize: 15,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </p>
          <p style={{ color: '#999', fontSize: 13, marginTop: 24 }}>
            Reference: <code>{postcardId}</code>
          </p>
        </>
      )}

      {status === 'error' && (
        <>
          <h1 style={{ fontSize: 22, margin: '0 0 12px' }}>Couldn't load your postcard</h1>
          <p style={{ color: '#666', lineHeight: 1.5 }}>{errorMsg}</p>
          <p style={{ color: '#999', fontSize: 13, marginTop: 24 }}>
            Reference: <code>{postcardId}</code>
          </p>
        </>
      )}
    </div>
  );
}
